import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import DialogActions from "@mui/material/DialogActions";
import ModalBasicLayout from "../ModalBasicLayout";
import SubtitlePreview from "./SubtitlePreview";
import Select from "../../Form/Select";
import Switch from "../../Form/Switch";
import ColorField from "../../Form/ColorField";
import ContainerModalActions from "../../Containers/ContainerModalActions";
import {
  MODAL_SUBTITLE_OPTIONS_TITLE,
  MODAL_SUBTITLE_OPTIONS_FONT_FAMILY,
  MODAL_SUBTITLE_OPTIONS_FONT_STYLE,
  MODAL_SUBTITLE_OPTIONS_SUBTITLE_COLOR,
  MODAL_SUBTITLE_OPTIONS_DO_HIGHLIGHT,
  MODAL_SUBTITLE_OPTIONS_HIGHLIGHT_COLOR,
  MODAL_SUBTITLE_OPTIONS_PRESET,
  MODAL_SUBTITLE_OPTIONS_PRESET_KARAOKE,
  MODAL_SUBTITLE_OPTIONS_PRESET_HIGHLIGHT,
  MODAL_SUBTITLE_OPTIONS_PRESET_BOXED,
  MODAL_SUBTITLE_OPTIONS_BOX_COLOR,
  MODAL_SUBTITLE_OPTIONS_PREVIEW_LABEL,
  BUTTON_SAVE,
  BUTTON_CANCEL,
} from "../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../lib/i18next";
import { useDispatchApp } from "../../../lib/redux";
import { startSaveSubtitlesConfiguration } from "../../../actions/user";
import {
  SUBTITLE_PRESET_BOXED,
  SUBTITLE_PRESET_HIGHLIGHT,
  SUBTITLE_PRESET_KARAOKE,
  PRESET_HIGHLIGHT_MODE,
  buildSubtitleOptionsPayload,
  getPresetVisualDefaults,
  resolveSubtitlePreset,
  BOXED_PRESET_COLORS,
  DEFAULT_SUBTITLE_OPTIONS,
} from "./subtitlePresets";

const SUBTITLE_FONT_OPTIONS = [
  { value: "Roboto", label: "Roboto" },
  { value: "Open Sans", label: "Open Sans" },
  { value: "Inter", label: "Inter" },
  { value: "Montserrat", label: "Montserrat" },
  { value: "Roboto Mono", label: "Roboto Mono" },
  { value: "Oswald", label: "Oswald" },
  { value: "Roboto Slab", label: "Roboto Slab" },
  { value: "Bebas Neue", label: "Bebas Neue" },
  { value: "Titillium Web", label: "Titillium Web" },
  { value: "Source Code Pro", label: "Source Code Pro" },
  { value: "Indie Flower", label: "Indie Flower" },
];

const loadedGoogleFonts = new Set();

const loadGoogleFont = (fontFamily) => {
  if (!fontFamily || loadedGoogleFonts.has(fontFamily)) return;
  loadedGoogleFonts.add(fontFamily);
  const familyParam = fontFamily.replace(/ /g, "+");
  const url = `https://fonts.googleapis.com/css2?family=${familyParam}:ital,wght@0,400;0,700;1,400;1,700&display=swap`;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = url;
  link.setAttribute("data-google-font", fontFamily);
  document.head.appendChild(link);
};

const SUBTITLE_FONT_STYLE_OPTIONS = [
  { value: "normal", label: "modalSubtitleOptionsFontStyleNormal" },
  { value: "bold", label: "modalSubtitleOptionsFontStyleBold" },
  { value: "italic", label: "modalSubtitleOptionsFontStyleItalic" },
  { value: "bold_italic", label: "modalSubtitleOptionsFontStyleBoldItalic" },
];

const SUBTITLE_PRESET_OPTIONS = [
  {
    value: SUBTITLE_PRESET_BOXED,
    label: MODAL_SUBTITLE_OPTIONS_PRESET_BOXED,
  },
  {
    value: SUBTITLE_PRESET_KARAOKE,
    label: MODAL_SUBTITLE_OPTIONS_PRESET_KARAOKE,
  },
  {
    value: SUBTITLE_PRESET_HIGHLIGHT,
    label: MODAL_SUBTITLE_OPTIONS_PRESET_HIGHLIGHT,
  },
];

const mergeInitialValues = (initialValues) => {
  const merged = { ...DEFAULT_SUBTITLE_OPTIONS, ...initialValues };
  const hasSavedStyle =
    Boolean(initialValues?.preset) || Boolean(initialValues?.highlightMode);
  const preset = resolveSubtitlePreset(hasSavedStyle ? initialValues : merged);
  const next = {
    ...merged,
    preset,
    highlightMode: PRESET_HIGHLIGHT_MODE[preset],
  };
  if (
    preset === SUBTITLE_PRESET_BOXED &&
    !next.hasBackground &&
    String(next.backgroundColor || "").toLowerCase() === "#000000"
  ) {
    next.subtitleColor = BOXED_PRESET_COLORS.subtitleColor;
    next.backgroundColor = BOXED_PRESET_COLORS.backgroundColor;
    next.hasBackground = true;
    next.doHighlight = true;
  }
  return next;
};

const ModalSubtitleOptions = ({
  modalOpen,
  onCloseModal,
  initialValues,
  onSave,
  persistAdvertiserConfiguration = true,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const { t } = useTranslationApp();
  const { control, handleSubmit, watch, reset, setValue, formState } = useForm({
    defaultValues: mergeInitialValues(initialValues),
    mode: "onChange",
  });
  const dispatch = useDispatchApp();
  const watchedValues = watch();
  const doHighlight = watchedValues.doHighlight;
  const preset = resolveSubtitlePreset(watchedValues);
  const isBoxed = preset === SUBTITLE_PRESET_BOXED;
  const { errors } = formState;

  useEffect(() => {
    if (modalOpen) {
      reset(mergeInitialValues(initialValues));
    }
  }, [modalOpen, initialValues, reset]);

  useEffect(() => {
    if (modalOpen && watchedValues.fontFamily) {
      loadGoogleFont(watchedValues.fontFamily);
    }
  }, [modalOpen, watchedValues.fontFamily]);

  const applyPreset = (nextPreset) => {
    const defaults = getPresetVisualDefaults(nextPreset);
    Object.entries(defaults).forEach(([key, value]) => {
      setValue(key, value);
    });
  };

  const onSubmit = async (data) => {
    setIsSaving(true);
    const payload = buildSubtitleOptionsPayload(data);
    if (persistAdvertiserConfiguration) {
      await dispatch(startSaveSubtitlesConfiguration(payload));
    }
    setIsSaving(false);
    onSave(payload);
    onCloseModal();
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      maxWidth="sm"
      scroll="paper"
      title={t(MODAL_SUBTITLE_OPTIONS_TITLE)}
    >
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        <Select
          name="preset"
          control={control}
          variant="filled"
          options={SUBTITLE_PRESET_OPTIONS}
          label={t(MODAL_SUBTITLE_OPTIONS_PRESET)}
          fullWidth
          size="small"
          doTranslate={true}
          errors={errors}
          rules={{ required: false }}
          actionsChange={applyPreset}
        />

        <Select
          name="fontFamily"
          control={control}
          variant="filled"
          options={SUBTITLE_FONT_OPTIONS}
          label={t(MODAL_SUBTITLE_OPTIONS_FONT_FAMILY)}
          fullWidth
          size="small"
          errors={errors}
          rules={{ required: false }}
        />

        <Select
          name="fontStyle"
          control={control}
          variant="filled"
          options={SUBTITLE_FONT_STYLE_OPTIONS}
          label={t(MODAL_SUBTITLE_OPTIONS_FONT_STYLE)}
          fullWidth
          size="small"
          doTranslate={true}
          errors={errors}
          rules={{ required: false }}
        />

        <ColorField
          control={control}
          name="subtitleColor"
          label={t(MODAL_SUBTITLE_OPTIONS_SUBTITLE_COLOR)}
          fullWidth
          size="small"
          errors={errors}
          rules={{ required: false }}
        />

        {isBoxed ? (
          <ColorField
            control={control}
            name="backgroundColor"
            label={t(MODAL_SUBTITLE_OPTIONS_BOX_COLOR)}
            fullWidth
            size="small"
            errors={errors}
            rules={{ required: false }}
          />
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box
                component="span"
                sx={{ fontWeight: 600, fontSize: 14, color: "text.primary" }}
              >
                {t(MODAL_SUBTITLE_OPTIONS_DO_HIGHLIGHT)}
              </Box>
              <Switch
                id="do-highlight-switch"
                isActive={!!doHighlight}
                size="small"
                hasLabel={false}
                onChangeIsActive={(checked) => setValue("doHighlight", checked)}
              />
            </Box>

            {doHighlight && (
              <ColorField
                control={control}
                name="highlightColor"
                label={t(MODAL_SUBTITLE_OPTIONS_HIGHLIGHT_COLOR)}
                fullWidth
                size="small"
                errors={errors}
                rules={{ required: false }}
              />
            )}
          </>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "text.primary" }}
          >
            {t(MODAL_SUBTITLE_OPTIONS_PREVIEW_LABEL)}
          </Typography>
          <SubtitlePreview options={watchedValues} />
        </Box>
      </Box>

      <DialogActions sx={{ px: 0, pt: 2, pb: 0 }}>
        <ContainerModalActions
          hasPadding={true}
          propsSecondaryButton={{
            showButton: true,
            onClick: onCloseModal,
            text: BUTTON_CANCEL,
          }}
          propsPrimaryButton={{
            showButton: true,
            onClick: handleSubmit(onSubmit),
            text: BUTTON_SAVE,
            type: "button",
            loading: isSaving,
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

export default ModalSubtitleOptions;
